import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
  Divider,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { Add, Delete, Info, School, Group, AttachFile, Save, Send } from "@material-ui/icons";

import {
  createStudentResearch,
  updateStudentResearch,
  submitRegistration,
} from "../../../../reducer/studentResearch/action";
import { getAllDepartments, getAllFaculties } from "../../../../reducer/department/action";
import { notification } from "../../../../reducer/home/action";
import { uploadMultiFile } from "../../../../reducer/file/action";
import select from "../../../../utils/select";
import { KPTextfield } from "../../../../components/KPTComponents";
import MultiFileManager from "../../../File/MultiFileManager/MultiFileManager";
import useDueWindow from "../../utils/useDueWindow";
import {
  DEFAULT_DURATION_MONTHS,
  STATUS_REGISTRATION_DRAFT,
  STATUS_REGISTRATION_ADJUST_REQUEST,
  STATUS_CONDUCTED_ADJUST_REQUEST,
  STUDENT_RESEARCH_STATUS_LABEL,
} from "../../../../constants/projectStatus";
import SectionCard from "../../utils/SectionCard";
import StudentResearchHistory from "../../utils/StudentResearchHistory";

// RegistrationForm — form gộp đăng ký đề tài + thành viên (R2).
// QUAN TRỌNG: KPTextfield không tự nhận prop `xs`, nên phải wrap
// <Grid item xs={N}> quanh mỗi field để chia cột. Pattern giống
// InstitutionalProject/DefaultForm.
export default function RegistrationForm({ detail, mode = "create", onDone }) {
  const dispatch = useDispatch();

  const profileList = useSelector((s) => select(s, "profileReducer", "list")) || [];
  // Khoa chủ quản CHỈ cho chọn Khoa chuyên môn (2026-09-11, đảo lại thay đổi
  // ngày 2026-08-14 từng mở cho mọi đơn vị). Endpoint /department/faculties đã
  // lọc `type = 'khoachuyenmon'` ở BE → không lọc lại ở FE.
  const faculties = useSelector((s) => select(s, "departmentReducer", "faculties")) || [];
  // Đơn vị đang gán của đề tài cũ có thể là phòng/ban (không nằm trong danh
  // sách Khoa). Vẫn phải hiển thị được, nếu không ô này trông như bị mất dữ
  // liệu — nên ghép thêm vào options và gắn cờ để ghi chú.
  const allDepartments = useSelector((s) => select(s, "departmentReducer", "departments")) || [];
  const profileDetail = useSelector((s) => select(s, "profileReducer", "detail"));

  useEffect(() => {
    if (faculties.length === 0) dispatch(getAllFaculties());
    // `departments` chỉ dùng để tra tên đơn vị cũ ngoài danh sách Khoa.
    if (allDepartments.length === 0) dispatch(getAllDepartments());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // detail.status có thể là string (từ BE) HOẶC array [{value, label, bgcolor}]
  // do StudentProjectTable.rowRender đã parse trước khi setDetail. Cần normalise
  // về string value cuối cùng để so sánh với constants.
  const extractStatus = (d) => {
    const s = d?.status;
    if (!s) return "";
    if (typeof s === "string") {
      const parts = s.split(",");
      return parts[parts.length - 1];
    }
    if (Array.isArray(s) && s.length > 0) {
      const last = s[s.length - 1];
      return typeof last === "object" ? last?.value : last;
    }
    return "";
  };
  const currentStatus = extractStatus(detail);

  const isView = mode === "view";
  // Trạng thái cho phép edit: đăng ký (draft / adjust) và conducted_adjust
  // (QLKH yêu cầu chỉnh sửa thực hiện → GV vẫn cần sửa thông tin đề tài).
  const isAdjustRequest =
    currentStatus === STATUS_REGISTRATION_ADJUST_REQUEST ||
    currentStatus === STATUS_CONDUCTED_ADJUST_REQUEST;
  const canEdit =
    !isView &&
    (mode === "create" ||
      currentStatus === STATUS_REGISTRATION_DRAFT ||
      isAdjustRequest);

  // Refactor 2026-05-21: thêm startTime/endTime — default startTime=hôm nay
  // (lúc tạo), endTime = start + durationMonths. Helper format YYYY-MM-DD
  // cho HTML input type=date.
  const todayISO = () => new Date().toISOString().substring(0, 10);
  const toDateInput = (raw) => {
    if (!raw) return "";
    const s = String(raw).substring(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : "";
  };
  const addMonthsISO = (startISO, months) => {
    if (!startISO) return "";
    const d = new Date(startISO);
    if (isNaN(d.getTime())) return "";
    d.setMonth(d.getMonth() + Number(months || 0));
    return d.toISOString().substring(0, 10);
  };

  const [values, setValues] = useState(() => {
    const start = toDateInput(detail?.startTime) || todayISO();
    return {
      id: detail?.id || 0,
      name: detail?.name || "",
      researchTarget: detail?.researchTarget || "",
      expectedResult: detail?.expectedResult || "",
      expectedCompetition: detail?.expectedCompetition || "",
      schoolYear: detail?.schoolYear || new Date().getFullYear(),
      departmentId: detail?.departmentId || 0,
      durationMonths: detail?.durationMonths || DEFAULT_DURATION_MONTHS,
      startTime: start,
      endTime: toDateInput(detail?.endTime) || addMonthsISO(start, detail?.durationMonths || DEFAULT_DURATION_MONTHS),
    };
  });

  // Danh sách chọn = 6 Khoa chuyên môn; nếu đề tài đang gán 1 đơn vị ngoài danh
  // sách (dữ liệu cũ) thì ghép thêm đúng đơn vị đó để không mất hiển thị.
  const departmentOptions = useMemo(() => {
    const list = faculties.filter((d) => d.id !== 0);
    const cur = values?.departmentId;
    if (cur && !list.some((d) => d.id === cur)) {
      const legacy = allDepartments.find((d) => d.id === cur);
      if (legacy) return [...list, { ...legacy, isLegacy: true }];
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faculties, allDepartments, values?.departmentId]);

  const [members, setMembers] = useState(detail?.researchMembers || []);
  const [instructors, setInstructors] = useState(detail?.instructors || []);
  const [saving, setSaving] = useState(false);

  // Mirror window guard từ BE (yearly_due_date_service.CheckWindow) — disable
  // nút "Nộp đăng ký" + tooltip lý do khi ngoài hạn. BE vẫn enforce, đây chỉ
  // là UX preview.
  const schoolYear = values.schoolYear || detail?.schoolYear || new Date().getFullYear();
  const { check: checkDue } = useDueWindow(schoolYear);
  const regWindow = checkDue("regStartTime", "regEndTime");

  // BE lưu mỗi field file là 1 string (URL đơn hoặc nhiều URL nối bằng "||",
  // giống pattern StudentGuidance). FE giữ array để hiển thị/edit qua
  // MultiFileManager, chỉ join lại khi build payload gửi BE.
  const parseUrls = (s) => (s ? String(s).split("||").filter(Boolean) : []);
  const [banDangKyUrls, setBanDangKyUrls] = useState(parseUrls(detail?.banDangKy));
  const [banDangKyNewFiles, setBanDangKyNewFiles] = useState([]);
  const [deCuongUrls, setDeCuongUrls] = useState(parseUrls(detail?.deCuongNghienCuu));
  const [deCuongNewFiles, setDeCuongNewFiles] = useState([]);

  useEffect(() => {
    if (detail) {
      setValues((v) => {
        const start = toDateInput(detail.startTime) || v.startTime || todayISO();
        return {
          ...v,
          id: detail.id || 0,
          name: detail.name || "",
          researchTarget: detail.researchTarget || "",
          expectedResult: detail.expectedResult || "",
          expectedCompetition: detail.expectedCompetition || "",
          schoolYear: detail.schoolYear || v.schoolYear,
          departmentId: detail.departmentId || 0,
          durationMonths: detail.durationMonths || DEFAULT_DURATION_MONTHS,
          startTime: start,
          endTime: toDateInput(detail.endTime) || addMonthsISO(start, detail.durationMonths || v.durationMonths),
        };
      });
      setMembers(detail.researchMembers || []);
      setInstructors(detail.instructors || []);
      setBanDangKyUrls(parseUrls(detail.banDangKy));
      setBanDangKyNewFiles([]);
      setDeCuongUrls(parseUrls(detail.deCuongNghienCuu));
      setDeCuongNewFiles([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail]);

  const handleChange = (key, val) => setValues({ ...values, [key]: val });

  // Upload các file đang chờ (newFiles) cho 1 field, merge với fileUrls hiện có,
  // trả về string "||"-joined để gửi BE. Đồng thời commit kết quả ngược lại 2
  // state setter để UI chuyển newFiles → fileUrls.
  // BE endpoint /file/storage/multi/{id} chỉ accept type thuộc enum
  // (xem file_controller.go @Enums) — module này dùng "studentResearch".
  const commitFiles = async (existingUrls, newFiles, setUrls, setNewFiles) => {
    if (!newFiles || newFiles.length === 0) return existingUrls.join("||");
    const ownerId = profileDetail?.id || 0;
    const result = await dispatch(uploadMultiFile(ownerId, newFiles, "studentResearch"));
    if (result?.success && Array.isArray(result.data)) {
      const merged = [...existingUrls, ...result.data];
      setUrls(merged);
      setNewFiles([]);
      return merged.join("||");
    }
    return existingUrls.join("||");
  };

  const addMember = () =>
    setMembers([
      ...members,
      {
        name: "", studentId: "", identityNumber: "", phoneNumber: "", email: "",
        class: "", academicYear: "", gpa: 0,
        researchRole: members.length === 0 ? "main" : "other",
      },
    ]);
  const removeMember = (idx) => setMembers(members.filter((_, i) => i !== idx));
  const updateMember = (idx, key, val) =>
    setMembers((prev) => {
      const next = prev.slice();
      next[idx] = { ...next[idx], [key]: val };
      return next;
    });

  const addInstructor = () =>
    setInstructors([...instructors, { name: "", profileId: 0, researchRole: "main" }]);
  const removeInstructor = (idx) => setInstructors(instructors.filter((_, i) => i !== idx));

  // updateInstructor cập nhật **một hoặc nhiều** field cùng lúc.
  // Nhận patch là object {key: val} → dùng functional setter để tránh
  // race khi 2 update liền nhau (ví dụ chọn GV: set profileId + name)
  // bị closure stale ghi đè lẫn nhau.
  const updateInstructor = (idx, patch) =>
    setInstructors((prev) => {
      const next = prev.slice();
      next[idx] = { ...next[idx], ...patch };
      return next;
    });

  // dateInputToISO — "YYYY-MM-DD" → RFC3339 UTC midnight cho BE (Go time.Time).
  const dateInputToISO = (s) => (s ? `${s}T00:00:00Z` : null);

  const buildPayload = (banDangKyStr, deCuongStr) => ({
    ...values,
    startTime: dateInputToISO(values.startTime),
    endTime:   dateInputToISO(values.endTime),
    banDangKy: banDangKyStr,
    deCuongNghienCuu: deCuongStr,
    researchMembers: members,
    instructors,
  });

  // Validate trước khi NỘP đăng ký. Lưu nháp thì không bắt buộc.
  // Trả về string lý do lỗi, hoặc null nếu OK.
  const validateForSubmit = () => {
    if (!values.name?.trim()) return "Vui lòng nhập Tên đề tài.";
    if (!values.departmentId) return "Vui lòng chọn Khoa chủ quản.";
    if (instructors.length === 0) return "Phải có ít nhất 1 giảng viên hướng dẫn.";
    if (instructors.some((ins) => !ins.profileId)) return "Vui lòng chọn giảng viên cho từng dòng (chưa chọn).";
    if (members.length === 0) return "Phải có ít nhất 1 thành viên sinh viên.";
    if (members.some((m) => !m.name?.trim() || !m.studentId?.trim() || !m.identityNumber?.trim()))
      return "Mỗi sinh viên cần điền Họ tên, MSSV và CCCD.";
    if (!members.some((m) => m.researchRole === "main"))
      return "Cần có 1 sinh viên giữ vai trò Chính.";
    if (banDangKyUrls.length + banDangKyNewFiles.length === 0)
      return "Vui lòng tải lên Bản đăng ký.";
    if (deCuongUrls.length + deCuongNewFiles.length === 0)
      return "Vui lòng tải lên Đề cương nghiên cứu.";
    return null;
  };

  const warn = (msg) => dispatch(notification({ type: "warning", message: msg }));

  // ensureSaved tạo (nếu chưa có id) hoặc cập nhật bản hiện tại. Trả id của bản
  // ghi sau khi đã lưu (để các action chuỗi như submit có thể dùng).
  const ensureSaved = async (banDangKyStr, deCuongStr) => {
    const payload = buildPayload(banDangKyStr, deCuongStr);
    if (values.id) {
      const ok = await dispatch(updateStudentResearch(values.id, payload));
      return ok ? values.id : null;
    }
    const entity = await dispatch(createStudentResearch(payload));
    if (!entity?.id) return null;
    // Đồng bộ id mới về local state để submit lần 2 không tạo bản trùng.
    setValues((v) => ({ ...v, id: entity.id }));
    return entity.id;
  };

  const handleSaveDraft = async () => {
    // Lưu nháp: chỉ cần Tên đề tài + Khoa (để có thể lưu rồi quay lại làm tiếp).
    if (!values.name?.trim()) return warn("Vui lòng nhập Tên đề tài để lưu nháp.");
    if (!values.departmentId) return warn("Vui lòng chọn Khoa chủ quản.");

    setSaving(true);
    try {
      const banDangKyStr = await commitFiles(banDangKyUrls, banDangKyNewFiles, setBanDangKyUrls, setBanDangKyNewFiles);
      const deCuongStr = await commitFiles(deCuongUrls, deCuongNewFiles, setDeCuongUrls, setDeCuongNewFiles);
      const id = await ensureSaved(banDangKyStr, deCuongStr);
      if (id && onDone) onDone();
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    const reason = validateForSubmit();
    if (reason) return warn(reason);

    setSaving(true);
    try {
      const banDangKyStr = await commitFiles(banDangKyUrls, banDangKyNewFiles, setBanDangKyUrls, setBanDangKyNewFiles);
      const deCuongStr = await commitFiles(deCuongUrls, deCuongNewFiles, setDeCuongUrls, setDeCuongNewFiles);
      // 1) Lưu (tạo mới hoặc update) → có id. 2) Gọi submit → status submitted.
      const id = await ensureSaved(banDangKyStr, deCuongStr);
      if (!id) return;
      const ok = await dispatch(submitRegistration(id));
      if (ok && onDone) onDone();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box padding="16px 24px" style={{ maxHeight: "calc(100vh - 220px)", overflow: "auto" }}>
      {/* Header banner (view mode) — mã đề tài + status chip. Hiển thị
          context nhanh khi user xem chi tiết đề tài đang ở phase nào. */}
      {isView && (detail?.researchCode || currentStatus) && (
        <Box
          display="flex"
          alignItems="center"
          gridGap={12}
          padding="8px 16px"
          marginBottom={2}
          bgcolor="#e3f2fd"
          border="solid 1px #90caf9"
          borderRadius={4}
        >
          {detail?.researchCode && (
            <Typography variant="body2">
              <span style={{ color: "#666" }}>Mã đề tài:</span>{" "}
              <b>{detail.researchCode}</b>
            </Typography>
          )}
          {currentStatus && (
            <Chip
              size="small"
              color="primary"
              label={STUDENT_RESEARCH_STATUS_LABEL[currentStatus] || currentStatus}
            />
          )}
        </Box>
      )}

      {/* Section 1 — Thông tin đề tài */}
      <SectionCard icon={<Info />} title="Thông tin đề tài" marginTop={0}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <KPTextfield
              id="name" required disabled={!canEdit}
              label="Tên đề tài" value={values.name}
              handleChangeForm={handleChange}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" style={{ marginBottom: 6 }}>
              Khoa chủ quản <span style={{ color: "#c62828" }}>*</span>
            </Typography>
            {/* Refactor 2026-05-19: Select → Autocomplete cho phép search nhanh
                khi danh sách khoa dài. */}
            <Autocomplete
              size="small"
              disabled={!canEdit}
              options={departmentOptions}
              getOptionLabel={(o) =>
                o?.name ? (o.isLegacy ? `${o.name} (không phải Khoa)` : o.name) : ""
              }
              value={departmentOptions.find((d) => d.id === values.departmentId) || null}
              onChange={(_, val) => handleChange("departmentId", val?.id || 0)}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" placeholder="Tìm Khoa..." />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" style={{ marginBottom: 6 }}>
              Thời gian thực hiện <span style={{ color: "#c62828" }}>*</span>
            </Typography>
            {/* Refactor 2026-05-14: đề tài SV cố định 6 tháng. Input số tháng
                cho phép admin chỉnh đặc biệt (3/9/12) nếu cần. Thay đổi duration
                tự re-compute endTime nếu startTime đã có. */}
            <Box display="flex" alignItems="center" gridGap={8}>
              <input
                type="number"
                min={1}
                max={24}
                step={1}
                value={values.durationMonths}
                disabled={!canEdit}
                onChange={(e) => {
                  const v = parseInt(e.target.value || DEFAULT_DURATION_MONTHS, 10);
                  if (!isNaN(v)) {
                    const newDur = Math.min(24, Math.max(1, v));
                    setValues((cur) => ({
                      ...cur,
                      durationMonths: newDur,
                      endTime: cur.startTime ? addMonthsISO(cur.startTime, newDur) : cur.endTime,
                    }));
                  }
                }}
                style={{
                  width: 80,
                  padding: 8,
                  fontSize: 14,
                  borderRadius: 4,
                  border: "1px solid rgba(0,0,0,0.23)",
                  outline: "none",
                  fontFamily: "inherit",
                }}
              />
              <Typography variant="body2" color="textSecondary">tháng (mặc định {DEFAULT_DURATION_MONTHS})</Typography>
            </Box>
          </Grid>
          {/* Refactor 2026-05-21: 2 ô ngày bắt đầu/kết thúc cho mỗi đề tài.
              Mặc định startTime = hôm nay khi tạo; endTime tự tính theo duration.
              Admin/CN có thể chỉnh trực tiếp khi cần (vd. lùi ngày bắt đầu). */}
          <Grid item xs={6}>
            <Typography variant="body2" style={{ marginBottom: 6 }}>
              Ngày bắt đầu <span style={{ color: "#c62828" }}>*</span>
            </Typography>
            <TextField
              fullWidth size="small" variant="outlined" type="date"
              value={values.startTime || ""}
              disabled={!canEdit}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => {
                const v = e.target.value;
                setValues((cur) => ({
                  ...cur,
                  startTime: v,
                  endTime: v ? addMonthsISO(v, cur.durationMonths) : cur.endTime,
                }));
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" style={{ marginBottom: 6 }}>
              Ngày kết thúc <span style={{ color: "#c62828" }}>*</span>
              <Typography component="span" variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
                (auto tính theo duration, có thể sửa)
              </Typography>
            </Typography>
            <TextField
              fullWidth size="small" variant="outlined" type="date"
              value={values.endTime || ""}
              disabled={!canEdit}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => handleChange("endTime", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <KPTextfield
              id="researchTarget" height={3} disabled={!canEdit}
              label="Mục tiêu nghiên cứu" value={values.researchTarget}
              handleChangeForm={handleChange}
            />
          </Grid>
          <Grid item xs={12}>
            <KPTextfield
              id="expectedResult" height={3} disabled={!canEdit}
              label="Dự kiến sản phẩm, kết quả" value={values.expectedResult}
              handleChangeForm={handleChange}
            />
          </Grid>
        </Grid>
      </SectionCard>

      {/* Section 2 — Giảng viên hướng dẫn */}
      <SectionCard icon={<School />} title="Giảng viên hướng dẫn">
        {instructors.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            Chưa có giảng viên hướng dẫn.
          </Typography>
        )}
        {instructors.map((ins, i) => (
          <React.Fragment key={`ins-${i}`}>
            {i > 0 && <Divider style={{ margin: "12px 0" }} />}
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={7}>
                <Typography variant="body2" style={{ marginBottom: 6 }}>
                  Tên giảng viên <span style={{ color: "#c62828" }}>*</span>
                </Typography>
                {/* Refactor 2026-05-19: Select → Autocomplete để tìm GV nhanh
                    theo tên / học vị (profileList có thể vài trăm record). */}
                <Autocomplete
                  size="small"
                  disabled={!canEdit}
                  options={profileList}
                  getOptionLabel={(o) =>
                    `${o?.name || ""}${o?.degree ? ` (${o.degree})` : ""}`
                  }
                  value={profileList.find((p) => p.id === ins.profileId) || null}
                  onChange={(_, val) =>
                    // Set cả profileId + name cùng 1 lần để tránh stale closure.
                    updateInstructor(i, { profileId: val?.id || 0, name: val?.name || "" })
                  }
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" placeholder="Tìm giảng viên..." />
                  )}
                />
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2" style={{ marginBottom: 6 }}>
                  Vai trò
                </Typography>
                <Select
                  fullWidth variant="outlined" margin="dense"
                  disabled={!canEdit}
                  value={ins.researchRole || "main"}
                  onChange={(e) => updateInstructor(i, { researchRole: e.target.value })}
                >
                  <MenuItem value="main">Chính</MenuItem>
                  <MenuItem value="sub">Phụ</MenuItem>
                </Select>
              </Grid>
              <Grid item xs={1} style={{ textAlign: "right" }}>
                {canEdit && (
                  <IconButton onClick={() => removeInstructor(i)} size="small">
                    <Delete fontSize="small" />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          </React.Fragment>
        ))}
        {canEdit && (
          <Box marginTop={2}>
            <Button size="small" startIcon={<Add />} onClick={addInstructor} variant="outlined">
              Thêm giảng viên
            </Button>
          </Box>
        )}
      </SectionCard>

      {/* Section 3 — Thành viên sinh viên */}
      <SectionCard icon={<Group />} title="Thành viên sinh viên (tối đa 5)">
        {members.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            Chưa có sinh viên tham gia.
          </Typography>
        )}
        {members.map((m, i) => (
          <Box
            key={`mem-${i}`}
            padding={2}
            marginTop={i === 0 ? 0 : 2}
            bgcolor="#fafafa"
            borderRadius={4}
            border="solid 1px #e0e0e0"
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
              <Typography variant="body2" color="textSecondary">
                <b>Sinh viên #{i + 1}</b>
              </Typography>
              {canEdit && (
                <IconButton size="small" onClick={() => removeMember(i)}>
                  <Delete fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Grid container spacing={2}>
              {/* Dòng 1: Họ tên + MSSV + Vai trò */}
              <Grid item xs={5}>
                <KPTextfield
                  id={`members.${i}.name`} required disabled={!canEdit}
                  label="Họ tên" value={m.name || ""}
                  handleChangeForm={(_, v) => updateMember(i, "name", v)}
                />
              </Grid>
              <Grid item xs={4}>
                <KPTextfield
                  id={`members.${i}.studentId`} required disabled={!canEdit}
                  label="MSSV" value={m.studentId || ""}
                  handleChangeForm={(_, v) => updateMember(i, "studentId", v)}
                />
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" style={{ marginBottom: 6 }}>
                  Vai trò <span style={{ color: "#c62828" }}>*</span>
                </Typography>
                {/* Refactor 2026-05-19: rename "Chủ nhiệm/Tham gia" → "Chính/Phụ"
                    cho thống nhất với GV hướng dẫn (main/sub vs main/other).
                    Value vẫn giữ "main"/"other" để không cần migration BE. */}
                <Select
                  fullWidth variant="outlined" margin="dense"
                  disabled={!canEdit}
                  value={m.researchRole || "other"}
                  onChange={(e) => updateMember(i, "researchRole", e.target.value)}
                >
                  <MenuItem value="main">Chính</MenuItem>
                  <MenuItem value="other">Phụ</MenuItem>
                </Select>
              </Grid>
              {/* Dòng 2: CCCD (required) + Lớp + Khóa + SĐT */}
              <Grid item xs={3}>
                <KPTextfield
                  id={`members.${i}.identityNumber`} required disabled={!canEdit}
                  label="CCCD" value={m.identityNumber || ""}
                  handleChangeForm={(_, v) => updateMember(i, "identityNumber", v)}
                />
              </Grid>
              <Grid item xs={3}>
                <KPTextfield
                  id={`members.${i}.class`} disabled={!canEdit}
                  label="Lớp" value={m.class || ""}
                  handleChangeForm={(_, v) => updateMember(i, "class", v)}
                />
              </Grid>
              <Grid item xs={3}>
                <KPTextfield
                  id={`members.${i}.academicYear`} disabled={!canEdit}
                  label="Khóa" value={m.academicYear || ""}
                  handleChangeForm={(_, v) => updateMember(i, "academicYear", v)}
                />
              </Grid>
              <Grid item xs={3}>
                <KPTextfield
                  id={`members.${i}.phoneNumber`} disabled={!canEdit}
                  label="SĐT" value={m.phoneNumber || ""}
                  handleChangeForm={(_, v) => updateMember(i, "phoneNumber", v)}
                />
              </Grid>
              {/* Dòng 3: GPA + Email */}
              <Grid item xs={3}>
                <KPTextfield
                  id={`members.${i}.gpa`} type="number" disabled={!canEdit}
                  label="GPA" value={m.gpa || 0}
                  handleChangeForm={(_, v) => updateMember(i, "gpa", Number(v))}
                />
              </Grid>
              <Grid item xs={9}>
                <KPTextfield
                  id={`members.${i}.email`} disabled={!canEdit}
                  label="Email" value={m.email || ""}
                  handleChangeForm={(_, v) => updateMember(i, "email", v)}
                />
              </Grid>
            </Grid>
          </Box>
        ))}
        {canEdit && members.length < 5 && (
          <Box marginTop={2}>
            <Button size="small" startIcon={<Add />} onClick={addMember} variant="outlined">
              Thêm sinh viên
            </Button>
          </Box>
        )}
      </SectionCard>

      {/* Section 4 — Hồ sơ đính kèm */}
      <SectionCard icon={<AttachFile />} title="Hồ sơ đính kèm">
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2" style={{ marginBottom: 6 }}>
              <b>Bản đăng ký</b> <span style={{ color: "#c62828" }}>*</span>
            </Typography>
            <MultiFileManager
              fileUrls={banDangKyUrls}
              setFileUrls={setBanDangKyUrls}
              newFiles={banDangKyNewFiles}
              setNewFiles={setBanDangKyNewFiles}
              disabled={!canEdit}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" style={{ marginBottom: 6 }}>
              <b>Đề cương nghiên cứu</b> <span style={{ color: "#c62828" }}>*</span>
            </Typography>
            <MultiFileManager
              fileUrls={deCuongUrls}
              setFileUrls={setDeCuongUrls}
              newFiles={deCuongNewFiles}
              setNewFiles={setDeCuongNewFiles}
              disabled={!canEdit}
            />
          </Grid>
        </Grid>
      </SectionCard>

      {/* Section 5 (mode=view only) — Timeline lịch sử lifecycle.
          Render mọi data đã nộp ở các phase sau đăng ký: BCTĐ, đơn dừng,
          tổng kết, hội đồng, điểm, giải, nhận xét. Mỗi section trong
          component conditional — chỉ hiện nếu có data tương ứng. */}
      {isView && <StudentResearchHistory detail={detail} />}

      {/* Action footer */}
      {canEdit && (
        <Box
          marginTop={3}
          paddingTop={2}
          display="flex"
          justifyContent="flex-end"
          gridGap={8}
          borderTop="solid 1px #e0e0e0"
        >
          <Button
            onClick={handleSaveDraft} disabled={saving}
            variant="outlined" color="primary" startIcon={<Save />}
          >
            Lưu nháp
          </Button>
          <Tooltip title={regWindow.isOpen ? "" : regWindow.reason || ""}>
            <span>
              <Button
                onClick={handleSubmit} disabled={saving || !regWindow.isOpen}
                variant="contained" color="primary" startIcon={<Send />}
              >
                {isAdjustRequest ? "Gửi lại sau chỉnh sửa" : "Nộp đăng ký"}
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}
